import {
  reconnectDelayMs,
  serverEnvelopeSchema,
  type ClientMessage,
  type ServerMessage,
} from '@bingo/shared';

/**
 * Cliente de WebSocket.
 *
 * Duas responsabilidades que valem o código: **reconectar sozinho** (o telão
 * fica horas ligado e a rede de salão cai) e **detectar salto de `seq`**.
 *
 * O servidor manda estado completo na conexão, então reconexão e salto de
 * sequência têm o MESMO tratamento — pedir `resync` e sobrescrever o estado.
 * É por isso que existe um único caminho de recuperação em vez de uma máquina
 * de estados de replay.
 */

export type ConnectionStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface RealtimeHandlers {
  onMessage: (message: ServerMessage) => void;
  onStatus?: (status: ConnectionStatus) => void;
}

export interface RealtimeOptions {
  /** `?eventId=` + access token do operador, ou `?dt=` do telão. */
  query: Record<string, string>;
  handlers: RealtimeHandlers;
}

export interface RealtimeConnection {
  send(message: ClientMessage): void;
  resync(): void;
  close(): void;
}

export function connectRealtime(options: RealtimeOptions): RealtimeConnection {
  const { query, handlers } = options;

  let socket: WebSocket | null = null;
  let attempt = 0;
  let closedByUs = false;
  let lastSeq = -1;
  let retryTimer: number | null = null;

  const url = () => {
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const params = new URLSearchParams(query);
    return `${protocol}//${location.host}/ws?${params.toString()}`;
  };

  const setStatus = (status: ConnectionStatus) => handlers.onStatus?.(status);

  const send = (message: ClientMessage) => {
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
  };

  const open = () => {
    setStatus(attempt === 0 ? 'connecting' : 'reconnecting');

    socket = new WebSocket(url());

    socket.onopen = () => {
      attempt = 0;
      setStatus('open');
    };

    socket.onmessage = (event) => {
      let envelope;
      try {
        envelope = serverEnvelopeSchema.parse(JSON.parse(String(event.data)));
      } catch {
        return; // fora do protocolo: ignora
      }

      // Salto de sequência = perdemos evento. Pede o estado completo, que é o
      // mesmo caminho da reconexão.
      if (lastSeq >= 0 && envelope.seq > lastSeq + 1 && envelope.payload.type !== 'state:sync') {
        send({ type: 'resync' });
      }

      lastSeq = envelope.seq;
      handlers.onMessage(envelope.payload);
    };

    socket.onclose = () => {
      if (closedByUs) {
        setStatus('closed');
        return;
      }

      attempt += 1;
      setStatus('reconnecting');
      // Backoff exponencial de 0,5s a 10s (definido no @bingo/shared).
      retryTimer = window.setTimeout(open, reconnectDelayMs(attempt));
    };

    socket.onerror = () => {
      // O `onclose` vem logo depois e é lá que a reconexão é agendada.
    };
  };

  open();

  return {
    send,
    resync: () => send({ type: 'resync' }),
    close() {
      closedByUs = true;
      if (retryTimer !== null) window.clearTimeout(retryTimer);
      socket?.close();
    },
  };
}
